import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useC304Query } from './hooks/useC304Query';
import type { ReportRow, UnitSummary, CurrencySummary } from '../../services/c304Api';
import './C304Form.css';

const C304Form: React.FC = () => {
  const { loading, data, unitSummary, groupCounts, currencySummary, queried, noData, runQuery, reset } = useC304Query();

  const [reportType, setReportType] = useState<string>('order');
  const [docType, setDocType] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFrom, setCustomerFrom] = useState('');
  const [customerTo, setCustomerTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [deliveryNoFrom, setDeliveryNoFrom] = useState('');
  const [deliveryNoTo, setDeliveryNoTo] = useState('');
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [perfCustFrom, setPerfCustFrom] = useState('');
  const [perfCustTo, setPerfCustTo] = useState('');

  const perfEnabled = docType === 'bulk' || docType === 'all';

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      doc_type: docType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      customer_from: customerFrom.trim() || undefined,
      customer_to: customerTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      delivery_no_from: deliveryNoFrom.trim() || undefined,
      delivery_no_to: deliveryNoTo.trim() || undefined,
      sales_from: salesFrom.trim() || undefined,
      sales_to: salesTo.trim() || undefined,
      perf_customer_from: perfEnabled && perfCustFrom.trim() ? perfCustFrom.trim() : undefined,
      perf_customer_to: perfEnabled && perfCustTo.trim() ? perfCustTo.trim() : undefined,
    });
  };

  const handleReset = () => {
    setReportType('order');
    setDocType('all');
    setDateFrom(''); setDateTo('');
    setCustomerFrom(''); setCustomerTo('');
    setProductFrom(''); setProductTo('');
    setDeliveryNoFrom(''); setDeliveryNoTo('');
    setSalesFrom(''); setSalesTo('');
    setPerfCustFrom(''); setPerfCustTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const rtLabels: Record<string, string> = { order: '單據別', customer: '客戶別', date: '日期別', product: '產品別', sales: '業務員別' };
    const title = `交運明細表（${rtLabels[reportType] || '單據別'}）`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>交運單號</th><th>客戶</th><th>交運日期</th><th>產品代號</th><th>顏色</th><th>幣別</th><th>數量</th><th>單位</th><th>單價</th><th>金額</th><th>本位幣金額</th>${reportType === 'sales' ? '<th>業務員</th>' : ''}</tr></thead><tbody>
${data.map(r => `<tr><td>${r.delivery_no}</td><td>${r.customer_name || r.customer_code}</td><td>${r.delivery_date}</td><td>${r.product_code}</td><td>${r.color_code} ${r.color_name}</td><td>${r.currency}</td><td class="right">${r.qty}</td><td>${r.unit}</td><td class="right">${r.unit_price?.toFixed(2)}</td><td class="right">${r.amount?.toFixed(2)}</td><td class="right">${r.nt_amount?.toFixed(2)}</td>${reportType === 'sales' ? `<td>${r.sales_name || r.sales_code}</td>` : ''}</tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const baseColumns: ColumnsType<ReportRow> = [
    { title: '交運單號', dataIndex: 'delivery_no', key: 'delivery_no', width: 100 },
    { title: '客戶代號', dataIndex: 'customer_code', key: 'customer_code', width: 100 },
    { title: '客戶名稱', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true, width: 140 },
    { title: '交運日期', dataIndex: 'delivery_date', key: 'delivery_date', width: 100 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '顏色', key: 'color', width: 80, render: (_: unknown, r: ReportRow) => r.color_code },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '數量', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' as const },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 50 },
    { title: '單價', dataIndex: 'unit_price', key: 'unit_price', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '金額', dataIndex: 'amount', key: 'amount', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '本位幣金額', dataIndex: 'nt_amount', key: 'nt_amount', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const salesColumns: ColumnsType<ReportRow> = [
    ...baseColumns,
    { title: '業務員', key: 'sales', width: 100, render: (_: unknown, r: ReportRow) => r.sales_name || r.sales_code },
  ];

  const columns = reportType === 'sales' ? salesColumns : baseColumns;

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const currColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: '原幣合計', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '本位幣合計', dataIndex: 'total_nt_amt', key: 'total_nt_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.C304} action="query">
      <div className="c304-container">
        <div className="c304-header">
          <h2>交運明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="order">單據別</Radio.Button>
              <Radio.Button value="customer">客戶別</Radio.Button>
              <Radio.Button value="date">日期別</Radio.Button>
              <Radio.Button value="product">產品別</Radio.Button>
              <Radio.Button value="sales">業務員別</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('單據類別')}
            <Radio.Group value={docType} onChange={e => setDocType(e.target.value)}>
              <Radio.Button value="sample">樣品單</Radio.Button>
              <Radio.Button value="bulk">大貨單</Radio.Button>
              <Radio.Button value="all">全部</Radio.Button>
              <Radio.Button value="inventory">庫存單</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('交運日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('交運日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('客戶代號起')}
              <Input value={customerFrom} onChange={e => setCustomerFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('客戶代號迄')}
              <Input value={customerTo} onChange={e => setCustomerTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('交運單號起')}
              <Input value={deliveryNoFrom} onChange={e => setDeliveryNoFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('交運單號迄')}
              <Input value={deliveryNoTo} onChange={e => setDeliveryNoTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('業務代號起')}
              <Input value={salesFrom} onChange={e => setSalesFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('業務代號迄')}
              <Input value={salesTo} onChange={e => setSalesTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('業績客戶起')}
              <Input value={perfCustFrom} onChange={e => setPerfCustFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear disabled={!perfEnabled} />
            </div>
            <div>
              {filterLabel('業績客戶迄')}
              <Input value={perfCustTo} onChange={e => setPerfCustTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear disabled={!perfEnabled} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                {reportType === 'sales' ? '業務員' : '客戶'}筆數：
                {groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.delivery_no}-${r.product_code}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: reportType === 'sales' ? 1400 : 1300 }}
              style={{ marginBottom: 16 }}
            />
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={unitSummary}
                  rowKey="unit"
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計</div>
                <Table<CurrencySummary>
                  columns={currColumns}
                  dataSource={currencySummary}
                  rowKey="currency"
                  size="small"
                  pagination={false}
                  scroll={{ x: 400 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default C304Form;
